import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  approveVaultClosure,
  cancelVaultClosure,
  getVaultClosureAvailable,
  requestVaultClosure,
} from 'src/pages/admin/services/vaultService';

import { VAULTS_NS as NS } from './constants';
import { type ClosureAction, closureErrorMessages, validateClosureReason } from './helpers';

export interface ClosureAlert {
  severity: 'error';
  messages: string[];
}

/**
 * XBIT-4243: the Close vault block's state — whether closing is open (`GET /vault/vaults/closure-available`, null while
 * unread or when the read fails, which disables the button), and the plain confirm for a request (with a reason), an
 * approval or a cancel. One request at a time; whatever the answer, the vault is read again (onSettled), so the block
 * shows what the engine now says. The Figma confirm dialog is owed (Module PRD §10); until it exists this is a plain one.
 */
export const useVaultClosure = ({
  vaultId,
  closureId,
  onSettled,
}: {
  vaultId: string;
  closureId: string | undefined;
  onSettled: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [available, setAvailable] = useState<boolean | null>(null);
  const [mode, setMode] = useState<ClosureAction | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [alert, setAlert] = useState<ClosureAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // From the submit until the vault has been read again: the block's controls are the old ones until then.
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const loadAvailability = useCallback(async () => {
    try {
      setAvailable(await getVaultClosureAvailable());
    } catch {
      // Unread is not "open": the button stays disabled rather than guess.
      setAvailable(null);
    }
  }, []);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability, vaultId]);

  const open = useCallback((next: ClosureAction) => {
    setReason('');
    setError(undefined);
    setAlert(null);
    setMode(next);
  }, []);

  const close = useCallback(() => {
    if (!inFlight.current) setMode(null);
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !mode) return;
    if (mode === 'request') {
      const invalid = validateClosureReason(reason);
      setError(invalid);
      if (invalid) return;
    } else if (!closureId) {
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setBusy(true);
    try {
      if (mode === 'request') await requestVaultClosure(vaultId, reason.trim());
      else if (mode === 'approve') await approveVaultClosure(vaultId, closureId as string);
      else await cancelVaultClosure(vaultId, closureId as string);
      setMode(null);
      notify(translate(`${NS}.detail.closure.done.${mode}`), { type: 'success' });
    } catch (err) {
      setAlert({ severity: 'error', messages: closureErrorMessages(err, translate) });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      try {
        await Promise.all([onSettled(), loadAvailability()]);
      } finally {
        setBusy(false);
      }
    }
  }, [closureId, loadAvailability, mode, notify, onSettled, reason, translate, vaultId]);

  return {
    available,
    mode,
    reason,
    setReason,
    error,
    alert,
    submitting,
    busy,
    open,
    close,
    submit,
  };
};

export type VaultClosureForm = ReturnType<typeof useVaultClosure>;
